// Document category derived from LOINC code (set by backend categorization pipeline)
export type DocumentCategory =
  | 'lab_result'
  | 'imaging_report'
  | 'clinical_note'
  | 'discharge_summary'
  | 'pathology_report'
  | 'other'
  | 'unknown'
  | null;  // null = not yet categorized

// LOINC code → category mapping (matches backend pipeline output)
const LOINC_TO_CATEGORY: Record<string, DocumentCategory> = {
  '11502-2': 'lab_result',
  '18748-4': 'imaging_report',
  '11506-3': 'clinical_note',
  '18842-5': 'discharge_summary',
  '26436-6': 'pathology_report',
  '11369-6': 'other',       // immunization (not in pipeline yet, map to other)
};

// Short badge labels for display
export const CATEGORY_BADGE_LABELS: Record<string, string> = {
  lab_result: 'Labs',
  imaging_report: 'Imaging',
  clinical_note: 'Clinical Note',
  discharge_summary: 'Discharge',
  pathology_report: 'Pathology',
  other: 'Other',
  unknown: 'Unknown',
};

// Badge color classes per category (Tailwind)
export const CATEGORY_BADGE_COLORS: Record<string, string> = {
  lab_result: 'bg-emerald-900/40 text-emerald-400 border-emerald-500/30',
  imaging_report: 'bg-blue-900/40 text-blue-400 border-blue-500/30',
  clinical_note: 'bg-violet-900/40 text-violet-400 border-violet-500/30',
  discharge_summary: 'bg-amber-900/40 text-amber-400 border-amber-500/30',
  pathology_report: 'bg-rose-900/40 text-rose-400 border-rose-500/30',
  other: 'bg-gray-800/40 text-gray-400 border-gray-500/30',
  unknown: 'bg-gray-800/40 text-gray-400 border-gray-500/30',
};

// Resolve LOINC code to document category
export function resolveDocumentCategory(loincCode: string | undefined): DocumentCategory {
  if (!loincCode) return null;
  // The uncategorized code means the pipeline hasn't run yet
  if (loincCode === '47420-5') return null;
  return LOINC_TO_CATEGORY[loincCode] ?? 'other';
}

// Medical record uploaded by patient
export interface MedicalRecordLocal {
  localId: string;
  fhirId?: string;          // DocumentReference ID
  binaryId?: string;        // Binary ID for URL construction
  filename: string;
  contentType: string;
  uploadedAt: string;
  url?: string;             // Full Binary URL for viewing
  isDeleted?: boolean;
  documentCategory: DocumentCategory; // Set by backend categorization pipeline
}

export interface MedicalRecordsData {
  records: MedicalRecordLocal[];
}

// For future AI categorization
export type RecordCategory =
  | 'lab-report'
  | 'imaging-report'
  | 'clinical-note'
  | 'pathology-report'
  | 'discharge-summary'
  | 'immunization-record'
  | 'other'
  | 'uncategorized';

// LOINC codes for future AI categorization
export const RECORD_CATEGORY_CODES: Record<RecordCategory, { system: string; code: string; display: string }> = {
  'lab-report': {
    system: 'http://loinc.org',
    code: '11502-2',
    display: 'Laboratory report',
  },
  'imaging-report': {
    system: 'http://loinc.org',
    code: '18748-4',
    display: 'Diagnostic imaging report',
  },
  'clinical-note': {
    system: 'http://loinc.org',
    code: '11506-3',
    display: 'Progress note',
  },
  'pathology-report': {
    system: 'http://loinc.org',
    code: '26436-6',
    display: 'Pathology report',
  },
  'discharge-summary': {
    system: 'http://loinc.org',
    code: '18842-5',
    display: 'Discharge summary',
  },
  'immunization-record': {
    system: 'http://loinc.org',
    code: '11369-6',
    display: 'History of immunization',
  },
  'other': {
    system: 'http://loinc.org',
    code: '47420-5',
    display: 'Functional status assessment note',
  },
  'uncategorized': {
    system: 'http://loinc.org',
    code: '47420-5',
    display: 'Patient-uploaded medical record',
  },
};

// Default code for patient uploads (before AI categorization)
export const PATIENT_UPLOAD_CODE = RECORD_CATEGORY_CODES['uncategorized'];

// Extension URL to track AI categorization status
export const AI_CATEGORIZED_EXTENSION_URL = 'http://precisionlongevity.io/fhir/StructureDefinition/ai-categorized';

// Accepted file types
export const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/heic',
];

// Human-readable file type labels
export const MIME_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG Image',
  'image/jpeg': 'JPEG Image',
  'image/heic': 'HEIC Image',
};

// Max file size: 25MB
export const MAX_FILE_SIZE = 25 * 1024 * 1024;

// Helper to check if content type is an image
export function isImageType(contentType: string): boolean {
  return contentType.startsWith('image/');
}

// Helper to check if content type is PDF
export function isPdfType(contentType: string): boolean {
  return contentType === 'application/pdf';
}

// Helper to get file type label
export function getFileTypeLabel(contentType: string): string {
  return MIME_TYPE_LABELS[contentType] || contentType;
}
